/**
* แปลและรับรองเอกสารทุกภาษา — 500+ Q&A dataset.
* Curated seeds + matrix generators (language × document, document × authority,
* language × certification route). Powers /translation/questions,
* /translation/q/$slug and the AI JSONL feed.
*/
import { MLT_LANGUAGES, MLT_DOC_TYPES } from "@/data/mlt/keywords-500";

export type MltQA = {
  slug: string;
  q: string;
  aShort: string;
  aLong: string;
  synonyms: string[];
  cluster:
    | "definition" | "price" | "speed" | "process" | "documents"
    | "language" | "authority" | "certification" | "compare"
    | "specialized" | "troubleshoot";
};

const PHONE = "094-895-8999";
const CTA = `Thai Notary Law · โทร ${PHONE} · LINE @Thainotary — แปลและรับรองครบวงจรทุกภาษา`;

const CURATED: MltQA[] = [
  {
    slug: "certified-translation-thailand-คืออะไร",
    q: "แปลรับรองเอกสาร (Certified Translation) คืออะไร?",
    aShort: "การแปลที่มีหนังสือรับรองความถูกต้องของคำแปลจากนักแปลหรือสำนักงานที่ได้รับการยอมรับ พร้อมลายเซ็นและตราประทับ ทำให้หน่วยงานราชการไทยและต่างประเทศรับพิจารณาเอกสารฉบับแปลได้",
    aLong: "คำแปลรับรองประกอบด้วย 3 ส่วน: (1) ตัวคำแปลที่จัดรูปแบบตรงกับต้นฉบับ (2) หนังสือรับรองความถูกต้อง (Certificate of Accuracy) ระบุชื่อผู้แปล คุณสมบัติ วันที่ และภาษาต้นทาง-ปลายทาง (3) ตราประทับ/ลายเซ็นผู้แปลหรือสำนักงาน · หากต้องใช้กับราชการไทยระดับสูงหรือส่งต่างประเทศ จะต่อด้วย Notary Public → กรมการกงสุล (MFA) → Apostille หรือสถานทูตปลายทาง · " + CTA,
    synonyms: ["certified translation คือ", "แปลรับรองคืออะไร", "certified translation thailand meaning"],
    cluster: "definition",
  },
  {
    slug: "แปลกี่ภาษา",
    q: "รับแปลเอกสารกี่ภาษา มีภาษาอะไรบ้าง?",
    aShort: "รองรับ 30+ ภาษาหลัก ได้แก่ อังกฤษ จีน ญี่ปุ่น เกาหลี เยอรมัน ฝรั่งเศส สเปน รัสเซีย อิตาลี อาหรับ เวียดนาม พม่า ลาว เขมร อินโดนีเซีย มาเลย์ ดัตช์ สวีเดน เดนมาร์ก ฟินแลนด์ ตุรกี ฮินดี โปรตุเกส และอื่น ๆ",
    aLong: "ทุกภาษาแปลโดยนักแปลที่มีประสบการณ์งานเอกสารราชการ และตรวจทานโดยเจ้าของภาษา (native reviewer) สำหรับงานที่ต้องยื่นสถานทูต · ภาษาหายาก (เนปาล ฮีบรู เปอร์เซีย เช็ก โปแลนด์) มีคิวเฉพาะ กรุณาสอบถามเวลาล่วงหน้า 1-2 วัน · " + CTA,
    synonyms: ["รับแปลภาษาอะไรบ้าง", "languages supported translation thailand", "แปลภาษาหายาก"],
    cluster: "language",
  },
  {
    slug: "ราคาแปลเอกสารต่อหน้า",
    q: "แปลเอกสารราคาต่อหน้าเท่าไหร่?",
    aShort: "ค่าบริการเสนอเป็นรายกรณี ขึ้นกับคู่ภาษา จำนวนหน้า ระดับความเฉพาะทางของเอกสาร กำหนดส่ง และชั้นการรับรองที่ปลายทางต้องใช้ — ขอใบเสนอราคาฟรีทางโทรศัพท์ LINE หรืออีเมลก่อนเริ่มงานได้ทุกครั้ง",
    aLong: "ปัจจัยที่ใช้ประเมิน: จำนวนหน้า A4 (ประมาณ 250 คำ/หน้า) · คู่ภาษาและความหาของภาษา · ประเภทเอกสาร (ทะเบียนราษฎรสั้น เทียบกับสัญญา คำพิพากษา งบการเงิน) · กำหนดส่ง (งานปกติหรือด่วน) · ชั้นการรับรองที่ต้องใช้ (รับรองคำแปล / Notary Public / กรมการกงสุล / สถานทูต) · ค่าธรรมเนียมนิติกรณ์ของกรมการกงสุลเป็นค่าธรรมเนียมราชการที่ประกาศไว้ 200 บาท/ตราประทับ (แบบด่วน 400 บาท) ตาม consular.mfa.go.th แยกจากค่าบริการแปล · " + CTA,
    synonyms: ["translation price per page thailand", "ค่าแปลเอกสารเท่าไหร่", "translation rate thailand"],
    cluster: "price",
  },
  {
    slug: "แปลเอกสารใช้เวลากี่วัน",
    q: "แปลและรับรองเอกสารใช้เวลากี่วัน?",
    aShort: "แปลอย่างเดียว 1-2 วันทำการ · แปล + Notary 2-3 วัน · แปล + กรมการกงสุล 4-6 วัน · แปล + Apostille/สถานทูต 5-10 วันทำการ",
    aLong: "ระยะเวลาเริ่มนับเมื่อได้รับต้นฉบับชัดเจนและยืนยันการสะกดชื่อตามพาสปอร์ต · งานด่วนภายใน 24 ชั่วโมงรองรับเฉพาะเอกสารสั้น ≤ 5 หน้า และภาษาหลัก · ขั้นตอนรับรองราชการมีคิวของหน่วยงาน จึงประเมินเป็นวันทำการเสมอ ไม่รวมวันหยุดราชการ · " + CTA,
    synonyms: ["translation turnaround thailand", "แปลเอกสารกี่วัน", "express translation 24 hours"],
    cluster: "speed",
  },
  {
    slug: "ขั้นตอนแปลและรับรอง",
    q: "ขั้นตอนแปลและรับรองเอกสารมีอะไรบ้าง?",
    aShort: "1) ส่งไฟล์ต้นฉบับ + สะกดชื่อตามพาสปอร์ต 2) รับใบเสนอราคาและยืนยันงาน 3) แปลและตรวจทาน 4) ออกหนังสือรับรองคำแปล 5) รับรอง Notary/กงสุล/Apostille ตามปลายทาง 6) จัดส่ง EMS หรือรับที่สำนักงาน",
    aLong: "ลำดับที่ถูกต้องสำคัญมาก: ต้องแปลและรับรองคำแปลก่อน จึงนำไปรับรองลายมือชื่อและนิติกรณ์ได้ · หากเอกสารมาจากต่างประเทศ ต้องผ่านการรับรองในประเทศต้นทาง (Apostille หรือสถานทูตไทย) ก่อนนำเข้ามาแปลในไทย มิฉะนั้นราชการไทยจะไม่รับ · " + CTA,
    synonyms: ["translation legalization steps thailand", "ขั้นตอนแปลรับรอง", "workflow certified translation"],
    cluster: "process",
  },
  {
    slug: "เอกสารที่ต้องเตรียม",
    q: "ต้องเตรียมอะไรบ้างก่อนส่งแปล?",
    aShort: "ต้นฉบับหรือสแกนคมชัดครบทุกหน้า · สำเนาหน้าพาสปอร์ตเพื่ออ้างอิงการสะกดชื่อ · ระบุหน่วยงานปลายทางและประเทศที่จะใช้ · แจ้งกำหนดส่งงาน",
    aLong: "สแกนควรเป็น 300 dpi ขึ้นไป ไม่ตัดขอบ ไม่มีเงาบัง เห็นตราประทับและลายเซ็นชัดเจน · ชื่อบุคคลต้องสะกดตรงกับพาสปอร์ต/ทะเบียนราษฎร 100% เพราะเป็นสาเหตุการถูกตีกลับอันดับหนึ่ง · หากมีคำแปลเดิมที่เคยใช้ผ่านแล้ว ส่งมาด้วยเพื่อรักษาความสอดคล้องของคำศัพท์ · " + CTA,
    synonyms: ["documents needed for translation", "เตรียมเอกสารแปล", "scan quality for translation"],
    cluster: "documents",
  },
  {
    slug: "notary-vs-sworn-vs-mfa",
    q: "Notary Public, Sworn Translator และรับรองกงสุล ต่างกันอย่างไร?",
    aShort: "Notary รับรองลายมือชื่อ/สำเนา · Sworn Translator คือนักแปลสาบานตนตามระบบยุโรปที่คำแปลมีผลทางกฎหมายในประเทศนั้น · กรมการกงสุล (MFA) รับรองนิติกรณ์เอกสารราชการไทยเพื่อใช้ต่างประเทศ",
    aLong: "เลือกตามปลายทาง: ยื่นในไทย → คำแปลรับรอง + Notary มักพอ · ยื่นสถานทูตต่างประเทศในไทย → คำแปล + MFA + สถานทูต · ยื่นราชการต่างประเทศ (ภาคีเฮก) → คำแปล + MFA + Apostille · ยื่นศาล/ทะเบียนราษฎรใน EU บางประเทศ → ต้องเป็น Sworn Translator ของประเทศนั้นเท่านั้น · " + CTA,
    synonyms: ["notary vs sworn translator", "ต่างกัน notary กงสุล", "mfa vs apostille translation"],
    cluster: "compare",
  },
  {
    slug: "เอกสารต่างประเทศใช้ในไทย",
    q: "เอกสารต่างประเทศจะนำมาใช้ในไทยต้องทำอย่างไร?",
    aShort: "รับรองในประเทศต้นทางก่อน (Apostille หรือสถานทูต/สถานกงสุลไทย) → นำเข้าไทย → แปลเป็นไทยพร้อมรับรองคำแปล → ยื่นกรมการกงสุลรับรองอีกชั้น → ใช้กับอำเภอ ตม. ศาล หรือ DBD",
    aLong: "ประเทศไทยเข้าเป็นภาคีอนุสัญญา Apostille แล้ว ทำให้เอกสารจากประเทศภาคีลดขั้นตอนลงมาก แต่กระบวนการแปลเป็นไทยและรับรองคำแปลยังจำเป็นเสมอเมื่อยื่นหน่วยงานไทย · เอกสารจากประเทศนอกภาคีต้องผ่านกระทรวงการต่างประเทศต้นทาง + สถานทูตไทยประจำประเทศนั้น · " + CTA,
    synonyms: ["foreign document use in thailand", "เอกสารต่างชาติใช้ในไทย", "apostille inbound thailand"],
    cluster: "certification",
  },
  {
    slug: "ชื่อสะกดไม่ตรงพาสปอร์ต",
    q: "ชื่อในคำแปลสะกดไม่ตรงพาสปอร์ตต้องทำอย่างไร?",
    aShort: "ต้องแก้ให้ตรงพาสปอร์ตก่อนยื่นเสมอ — แจ้งเราพร้อมสำเนาพาสปอร์ต เราจะแก้และออกคำแปลรับรองฉบับใหม่ หากประทับตราราชการไปแล้วต้องยื่นรับรองใหม่ทั้งชุด",
    aLong: "หน่วยงานไทยและสถานทูตตรวจการสะกดชื่อแบบตัวต่อตัว รวมถึงลำดับชื่อกลางและเครื่องหมายกำกับเสียง · กรณีเอกสารเก่าที่สะกดต่างจากพาสปอร์ตปัจจุบัน แนะนำแนบหนังสือรับรองการเป็นบุคคลเดียวกันหรือใบเปลี่ยนชื่อฉบับแปล · " + CTA,
    synonyms: ["name mismatch translation passport", "สะกดชื่อไม่ตรง", "spelling correction certified translation"],
    cluster: "troubleshoot",
  },
  {
    slug: "แปลเอกสารเฉพาะทาง",
    q: "รับแปลงานเฉพาะทาง เช่น สิทธิบัตร การแพทย์ วิศวกรรม ไหม?",
    aShort: "รับแปล — สิทธิบัตร คู่มือเครื่องจักร รายงาน EIA เอกสารขึ้นทะเบียนยา อย. งบการเงินตรวจสอบแล้ว สัญญาแฟรนไชส์ และเอกสารอนุญาโตตุลาการระหว่างประเทศ โดยนักแปลเฉพาะสาขา",
    aLong: "งานเฉพาะทางใช้ทีมสองชั้น: นักแปลสาขา + ผู้ตรวจทานด้านเทคนิค/กฎหมาย พร้อมจัดทำ glossary คำศัพท์เพื่อความสอดคล้องทั้งชุดเอกสาร · รองรับ NDA และการจัดการข้อมูลลับตามมาตรฐาน PDPA · " + CTA,
    synonyms: ["technical translation thailand", "patent translation thailand", "medical document translation"],
    cluster: "specialized",
  },
];

const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9ก-๙]+/g, "-").replace(/^-|-$/g, "");

/** Matrix A: language × document (30 × 25 = 750) — the core long-tail engine. */
const LANG_DOC: MltQA[] = MLT_LANGUAGES.flatMap((l) =>
  MLT_DOC_TYPES.map((d) => ({
    slug: slugify(`mlt-${l.slug}-${d.slug}`),
    q: `แปล${d.th}ภาษา${l.th}พร้อมรับรอง ใช้ในไทยได้ไหม?`,
    aShort: `ได้ — เรารับแปล${d.th}จากภาษา${l.th}เป็นไทย (และไทยเป็น${l.th}) พร้อมหนังสือรับรองคำแปล ใช้ยื่นอำเภอ ตม. ศาล DBD และสถานทูตได้ ระยะเวลา 1-3 วันทำการ`,
    aLong: `งานแปล ${d.en} คู่ภาษา ${l.en}–Thai ดำเนินการโดยนักแปลที่ชำนาญเอกสารราชการ ตรวจทานการสะกดชื่อให้ตรงพาสปอร์ต และคงรูปแบบตาราง ตราประทับ และลายเซ็นตามต้นฉบับ · หากเอกสารออกจากต่างประเทศต้องผ่านการรับรองต้นทาง (Apostille หรือสถานทูตไทย) ก่อนนำมาแปลใช้ในไทย · ต่อยอดรับรอง Notary Public / กรมการกงสุล / Apostille ได้ในที่เดียว · ${CTA}`,
    synonyms: [
      `${l.en.toLowerCase()} ${d.en} translation thailand`,
      `แปล${d.th}ภาษา${l.th}`,
      `certified ${d.en} translation ${l.en.toLowerCase()}`,
    ],
    cluster: "language" as const,
  })),
);

/** Matrix B: document × receiving authority. */
const AUTHORITIES = [
  { key: "amphoe", label: "สำนักงานเขต/อำเภอ", ans: "ต้องคำแปลภาษาไทยพร้อมรับรองคำแปล และเอกสารต่างประเทศต้องผ่าน Apostille หรือสถานทูตไทยจากต้นทางก่อน" },
  { key: "immigration", label: "สำนักงานตรวจคนเข้าเมือง", ans: "ใช้คำแปลไทยรับรองคำแปล แนบต้นฉบับและสำเนาพาสปอร์ตผู้ยื่นทุกครั้ง" },
  { key: "court", label: "ศาลไทย", ans: "ศาลรับคำแปลที่มีหนังสือรับรองความถูกต้อง แนะนำให้ทนายความตรวจศัพท์กฎหมายก่อนยื่น" },
  { key: "dbd", label: "กรมพัฒนาธุรกิจการค้า (DBD)", ans: "ต้องคำแปลไทยรับรองคำแปล และเอกสารบริษัทต่างชาติต้องรับรองจากต้นทางครบชั้น" },
  { key: "land-office", label: "กรมที่ดิน", ans: "ตรวจเข้มลายมือชื่อ แนะนำแปล + Notary รับรองลายเซ็นผู้มอบอำนาจ" },
  { key: "embassy", label: "สถานทูตในประเทศไทย", ans: "ส่วนใหญ่ต้องคำแปลภาษาอังกฤษหรือภาษาราชการของประเทศนั้น + รับรองกรมการกงสุลก่อน" },
  { key: "mfa", label: "กรมการกงสุล (MFA)", ans: "รับรองนิติกรณ์คำแปลได้เมื่อคำแปลตรงต้นฉบับและสะกดชื่อตรงพาสปอร์ต" },
  { key: "work-permit", label: "ขอใบอนุญาตทำงาน", ans: "ใช้คำแปลไทยรับรองคำแปลของวุฒิการศึกษาและหนังสือรับรองประสบการณ์" },
];

const DOC_AUTHORITY: MltQA[] = MLT_DOC_TYPES.flatMap((d) =>
  AUTHORITIES.map((a) => ({
    slug: slugify(`mlt-${d.slug}-${a.key}`),
    q: `แปล${d.th}เพื่อยื่น${a.label} ต้องทำอย่างไร?`,
    aShort: `${a.ans} — เรารับแปล${d.th}ทุกภาษาพร้อมหนังสือรับรองคำแปล และประสานงานรับรองต่อให้ครบสาย`,
    aLong: `เส้นทางมาตรฐานสำหรับ ${d.en} → ${a.label}: ตรวจต้นฉบับ → แปลและตรวจทาน → ออกหนังสือรับรองคำแปล → รับรองเพิ่มเติมตามที่หน่วยงานกำหนด (Notary / กรมการกงสุล / Apostille / สถานทูต) → ยื่นหรือจัดส่ง · เราตรวจ checklist ของหน่วยงานให้ก่อนยื่นทุกครั้ง เพื่อลดโอกาสถูกตีกลับ · ${CTA}`,
    synonyms: [
      `${d.en} translation for ${a.key} thailand`,
      `แปล${d.th}ยื่น${a.label}`,
      `certified ${d.en} translation thailand`,
    ],
    cluster: "authority" as const,
  })),
);

/** Matrix C: language × certification route. */
const ROUTES = [
  { key: "notary", label: "รับรอง Notary Public", ans: "แปลก่อน แล้วให้ทนายผู้ทำคำรับรองลายมือชื่อ (Notarial Services Attorney) รับรองลายเซ็นผู้แปลหรือผู้ลงนาม" },
  { key: "mfa", label: "รับรองกรมการกงสุล", ans: "ยื่นคำแปลพร้อมต้นฉบับที่กรมการกงสุล แจ้งวัฒนะ ใช้เวลา 2-4 วันทำการ หรือแบบด่วน 1 วัน" },
  { key: "apostille", label: "ขอ Apostille", ans: "ประเทศไทยยังไม่ออกตรา Apostille — อนุสัญญากรุงเฮก ค.ศ. 1961 จะมีผลบังคับใช้กับไทยวันที่ 28 กุมภาพันธ์ 2027 ระหว่างนี้ยังใช้สายรับรองเดิมคือกรมการกงสุลแล้วต่อสถานทูตปลายทางเมื่อประเทศนั้นกำหนด" },
  { key: "embassy", label: "รับรองสถานทูตปลายทาง", ans: "สำหรับประเทศนอกภาคีเฮก ต้องผ่านกรมการกงสุลก่อน แล้วจึงยื่นสถานทูตของประเทศนั้นในไทย" },
];

const LANG_ROUTE: MltQA[] = MLT_LANGUAGES.flatMap((l) =>
  ROUTES.map((r) => ({
    slug: slugify(`mlt-${l.slug}-${r.key}`),
    q: `แปลภาษา${l.th}แล้ว${r.label} ทำอย่างไร?`,
    aShort: `${r.ans} — คู่ภาษา ${l.en}–Thai เราดำเนินการแปลและเดินเรื่องรับรองให้ครบในที่เดียว`,
    aLong: `แนวทางสำหรับคู่ภาษา ${l.en}: บางหน่วยงานปลายทางกำหนดให้คำแปลต้องเป็นภาษาอังกฤษหรือภาษาราชการของประเทศนั้น จึงควรยืนยันภาษาปลายทางก่อนเริ่มงาน เพื่อไม่ต้องแปลซ้ำ · เราตรวจสอบข้อกำหนดล่าสุดของหน่วยงานและสถานทูตก่อนยื่นทุกครั้ง · ${CTA}`,
    synonyms: [
      `${l.en.toLowerCase()} translation ${r.key} thailand`,
      `แปล${l.th} ${r.label}`,
      `${r.key} certified translation thailand`,
    ],
    cluster: "certification" as const,
  })),
);

import { MLT_QA_WAVE2 } from "./questions-wave2";
import { MLT_QA_WAVE3 } from "./questions-wave3";

export const MLT_QA: MltQA[] = [
  ...CURATED,
  ...MLT_QA_WAVE2,
  ...MLT_QA_WAVE3,
  ...LANG_DOC,
  ...DOC_AUTHORITY,
  ...LANG_ROUTE,
];
export const MLT_QA_BY_SLUG: Record<string, MltQA> = Object.fromEntries(
  MLT_QA.map((q) => [q.slug, q]),
);
export const MLT_QA_TOTAL = MLT_QA.length;
